/**
 * Shared wrapper component for all cell editors
 * Provides the backdrop, action buttons, and positioning logic
 */

import React, { useCallback, useEffect, useRef } from "react";
import { useZoom } from "@/providers/ZoomProvider";

interface CellEditorWrapperProps {
  children: React.ReactNode;
  isVisible: boolean;
  cellRect: DOMRect | null;
  cellWidth: number;
  onSave: () => void;
  onCancel: () => void;
  minHeight?: number;
  maxHeight?: number;
}

const CellEditorWrapper: React.FC<CellEditorWrapperProps> = ({
  children,
  isVisible,
  cellRect,
  cellWidth,
  onSave,
  onCancel,
  minHeight = 44,
  maxHeight = 300,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { zoom } = useZoom();

  // Calculate editor position (adjusted for zoom)
  // When zoom is applied to the container, fixed positioning is also affected
  // We need to divide by zoom to compensate
  const getEditorStyle = useCallback((): React.CSSProperties => {
    if (!cellRect || !isVisible) {
      return { display: "none" };
    }

    const computedMinHeight = Math.max(minHeight, cellRect.height);

    return {
      position: "fixed",
      top: `${cellRect.top / zoom}px`,
      left: `${cellRect.left / zoom}px`,
      width: `${cellWidth / zoom}px`,
      minHeight: `${computedMinHeight / zoom}px`,
      maxHeight: `${maxHeight / zoom}px`,
      zIndex: 9999,
      backgroundColor: "hsl(var(--card))",
      border: "2px solid hsl(var(--primary))",
      borderRadius: "4px",
      boxShadow: "0 4px 16px hsl(var(--foreground) / 0.15)",
      overflow: "auto",
      fontSize: "14px",
      fontFamily: "inherit",
      lineHeight: "1.5",
    };
  }, [cellRect, isVisible, cellWidth, minHeight, maxHeight, zoom]);

  // Calculate button position (adjusted for zoom)
  const getButtonStyle = useCallback((): React.CSSProperties => {
    if (!cellRect || !isVisible) {
      return { display: "none" };
    }

    return {
      position: "fixed",
      top: `${(cellRect.top - 36) / zoom}px`,
      left: `${(cellRect.left + cellWidth - 120) / zoom}px`,
      width: `${120 / zoom}px`,
      zIndex: 10000,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      backgroundColor: "hsl(var(--card))",
      border: "1px solid hsl(var(--border))",
      borderRadius: "4px",
      boxShadow: "0 2px 8px hsl(var(--foreground) / 0.1)",
      padding: "4px 8px",
    };
  }, [cellRect, isVisible, cellWidth, zoom]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isVisible &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        const target = event.target as Element;
        const isActionButton = target.closest(".mt-cell-editor-actions");

        if (!isActionButton) {
          onCancel();
        }
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isVisible, onCancel]);

  if (!isVisible || !cellRect) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-foreground/[0.02]"
        style={{
          zIndex: 9998,
        }}
      />

      {/* Action buttons */}
      <div className="mt-cell-editor-actions" style={getButtonStyle()}>
        <button
          onClick={onCancel}
          className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors border border-border rounded bg-card hover:bg-muted"
          title="Cancel (Esc)"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          className="px-2 py-1 text-xs text-primary-foreground bg-primary hover:bg-primary/90 transition-colors border border-primary rounded"
          title="Save (Ctrl+Enter)"
        >
          Save
        </button>
      </div>

      {/* Editor container */}
      <div ref={containerRef} style={getEditorStyle()}>
        {children}
      </div>
    </>
  );
};

export default CellEditorWrapper;

